/**
 * WelcomeModal — 60-second intro dla nowych użytkowników MASKIL.
 *
 * Adapted from LES-LEX OnboardingWizard pattern + per-persona routing.
 *
 * Design principles:
 *   - Max 2 zdania per step (no jargon)
 *   - Każdy step ma "benefit badge" (oszczędzisz 2h, ROI 17x)
 *   - 7 steps dla każdej persony, customized content
 *   - Visual icon + progress dots
 *   - Skip "Pokaż później" (soft, nie permanent)
 *   - Po complete → init DEMO clone + redirect do Mission Board
 *   - localStorage cache + backend state sync
 *
 * Personas:
 *   - bidder    → focus: BoQ → RFQ → Offer → JEDZ
 *   - investor  → focus: ROI Radar + multi-industry
 *   - producer  → focus: catalog upload + placement dashboard
 *
 * Auto-show logic:
 *   - On first login after onboarding wizard
 *   - GET /tutorial/state → if welcome_seen=false → show
 *   - Dismiss → POST /tutorial/welcome-seen + don't show again
 *   - Resume → Settings → "Pokaż samouczek" → POST /tutorial/restart
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Sparkles, FileText, Target, Truck, Send, Award, Check,
  ChevronRight, ChevronLeft, X, Clock, TrendingUp, Building2, Coins, Factory, Lightbulb
} from 'lucide-react';
import { api } from '../../lib/api';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);

// 2026-05-27 (T7/C onboarding-simplification): zredukowane z 7 kroków
// per-persona do 3 uniwersalnych. Powód: time-to-first-value, drop-off Izy.
// Stare per-persona variants (bidder/investor/producer) zachowane jako
// `getStepsForPersonaLegacy` poniżej — gdyby trzeba było wrócić.
function getStepsForPersona(persona, userName, lang) {
  const greeting = userName
    ? t(lang, `Cześć ${userName}!`, `Hi ${userName}!`)
    : t(lang, 'Cześć!', 'Hello!');

  // Mini-persona-tweak: dla designer subtelnie inny opis value-prop
  const isDesigner = persona === 'investor';  // designer mapuje na investor
  const valueProp = isDesigner
    ? t(lang,
        'Znajdziesz inwestycje GUNB i sieć architektów, a Adam pomoże w RFQ do dostawców.',
        'Find GUNB investments and architects network, Adam helps with supplier RFQs.')
    : t(lang,
        'Znajdzie przetargi, zanalizuje SWZ i pomoże Ci je wygrać — bez 2 dni klikania.',
        'Finds tenders, analyzes SWZ, helps you win — without 2 days of clicking.');

  return [
    {
      icon: Sparkles,
      color: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
      title: greeting + ' ' + t(lang, 'Witaj w MASKIL', 'Welcome to MASKIL'),
      description: valueProp,
      benefit: null,
    },
    {
      icon: Target,
      color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300',
      title: t(lang, 'Mam dla Ciebie gotowy przykład', 'Pre-built example ready for you'),
      description: t(lang,
        'Przetarg DEMO — oświetlenie 850k PLN. Przejdziesz pełen flow (znajdź → analiza → RFQ → oferta) w 10 minut. Zobaczysz wszystko co MASKIL robi.',
        'DEMO tender — street lighting 850k PLN. Full flow (find → analyze → RFQ → offer) in 10 minutes. See everything MASKIL does.'),
      benefit: t(lang, '~10 min — bez ryzyka, na realnych danych', '~10 min — no risk, real data'),
    },
    {
      icon: Check,
      color: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-300',
      title: t(lang, 'Mission Board prowadzi Cię dalej', 'Mission Board guides you forward'),
      description: t(lang,
        'W prawym dolnym rogu widzisz Mission Board z opcjonalnymi misjami. Pomijaj, wracaj, ignoruj — Twoja sprawa. Resetujesz w Ustawieniach.',
        'Bottom-right corner shows Mission Board with optional missions. Skip, return, ignore — your choice. Reset in Settings.'),
      benefit: null,
    },
  ];
}


// Legacy 7-step variants — zostawione na wypadek powrotu, NIE używane od T7/C.
// Wywołaj `getStepsForPersonaLegacy(persona, userName, lang)` aby przywrócić.
function getStepsForPersonaLegacy(persona, userName, lang) {
  const greeting = userName
    ? t(lang, `Cześć ${userName}!`, `Hi ${userName}!`)
    : t(lang, 'Cześć!', 'Hello!');

  if (persona === 'investor') {
    return [
      {
        icon: Sparkles,
        color: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300',
        title: greeting + ' ' + t(lang, 'Witaj w MASKIL', 'Welcome to MASKIL'),
        description: t(lang,
          'Multi-industry ROI Radar — pokaże Ci tylko te przetargi, w których zarobisz >25%. Pominę resztę.',
          'Multi-industry ROI Radar — only shows tenders with >25% margin. Skip the rest.'),
        benefit: null,
      },
      {
        icon: Target,
        color: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
        title: '1. ' + t(lang, 'ROI Radar — wybierasz okazje', 'ROI Radar — pick deals'),
        description: t(lang,
          'Wszystkie 12 branż w jednym widoku. Sortowanie po ROI%. Filtr risk profile.',
          'All 12 industries in one view. Sort by ROI%. Filter risk profile.'),
        benefit: t(lang, 'Tylko top 5% deali — zero marnowania czasu', 'Top 5% deals only — zero time wasted'),
      },
      {
        icon: FileText,
        color: 'bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-300',
        title: '2. ' + t(lang, 'SPHINX due diligence w 60s', 'SPHINX due diligence in 60s'),
        description: t(lang,
          'Klik na zamawiającego → 18 providerów (KRS, CRBR, sankcje EU/OFAC, Altman Z, OSINT) → pełny raport w minutę.',
          'Click authority → 18 providers (KRS, CRBR, sanctions EU/OFAC, Altman Z, OSINT) → full report in a minute.'),
        benefit: t(lang, 'Zewn. due diligence kosztuje 200 PLN/raport — u nas wliczone', 'External due diligence costs 200 PLN/report — included here'),
      },
      {
        icon: Truck,
        color: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300',
        title: '3. ' + t(lang, 'Pełen pipeline na demo', 'Full pipeline on demo'),
        description: t(lang,
          'Mamy gotowy przykład: oświetlenie Pyrzyce 850k PLN. Cały flow od analizy SWZ przez RFQ do JEDZ XML.',
          'Pre-built example: street lighting Pyrzyce 850k PLN. Full flow from SWZ analysis through RFQ to JEDZ XML.'),
        benefit: t(lang, '15 minut — zobaczysz wszystko co MASKIL umie', '15 minutes — see everything MASKIL does'),
      },
      {
        icon: Send,
        color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900 dark:text-cyan-300',
        title: '4. ' + t(lang, 'Wbudowana skrzynka pocztowa', 'Built-in inbox'),
        description: t(lang,
          'RFQ do dostawców z jednego miejsca. Auto-parsowanie quote response. Bez przełączania na Gmail.',
          'RFQ to suppliers from one place. Auto-parse quote responses. No Gmail switching.'),
        benefit: t(lang, 'Single pane of glass — wszystko w MASKIL', 'Single pane of glass — everything in MASKIL'),
      },
      {
        icon: TrendingUp,
        color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-300',
        title: '5. ' + t(lang, 'Genome uczy się z Twoich wygranych', 'Genome learns from your wins'),
        description: t(lang,
          'Po każdym won/lost → algorytm rekalkuluje ROI predictions. Z czasem coraz lepsze rekomendacje.',
          'After each won/lost → algorithm recalibrates ROI predictions. Recommendations get better over time.'),
        benefit: t(lang, 'Twój prywatny edge — konkurencja nie wie jak liczysz', 'Your private edge — competition does not know your math'),
      },
      {
        icon: Check,
        color: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300',
        title: t(lang, 'Gotowy!', 'Ready!'),
        description: t(lang,
          'Zacznę od pokazania pełnego pipeline na Pyrzycach 850k. Mission Board pokaże dalsze kroki.',
          'I will start by showing the full pipeline on Pyrzyce 850k. Mission Board shows next steps.'),
        benefit: null,
      },
    ];
  }

  if (persona === 'producer') {
    return [
      {
        icon: Factory,
        color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-300',
        title: greeting + ' ' + t(lang, 'Witaj w MASKIL', 'Welcome to MASKIL'),
        description: t(lang,
          'Twoje produkty trafią do shortlist setek handlowców startujących w przetargach. Płacisz tylko za wygrane.',
          'Your products in shortlists of hundreds of bidders. Pay only for wins.'),
        benefit: null,
      },
      {
        icon: FileText,
        color: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
        title: '1. ' + t(lang, 'Wgraj katalog produktów', 'Upload product catalog'),
        description: t(lang,
          'CSV lub Excel — AI rozpozna kolumny (cena, lumeny, IP, certyfikaty). 200 produktów w 5 minut.',
          'CSV or Excel — AI detects columns (price, lumens, IP, certs). 200 products in 5 min.'),
        benefit: t(lang, 'Zero ręcznego wpisywania — AI parsuje wszystko', 'Zero manual entry — AI parses everything'),
      },
      {
        icon: Target,
        color: 'bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-300',
        title: '2. ' + t(lang, 'Placements dashboard', 'Placements dashboard'),
        description: t(lang,
          'Widzisz w czasie rzeczywistym: gdzie Twoje produkty znalazły się w shortlistach. Geo heat map Polska.',
          'See real-time: where your products appear in shortlists. Geo heat map of Poland.'),
        benefit: t(lang, 'Wiedza której konkurencja nie ma — sygnał gdzie skupić sprzedaż', 'Intel competition lacks — where to focus sales'),
      },
      {
        icon: Coins,
        color: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300',
        title: '3. ' + t(lang, 'Finder fee tylko za wygrane', 'Finder fee only on wins'),
        description: t(lang,
          '3-5% wartości deal — fakturujemy tylko gdy bidder wygra przetarg z Twoim produktem. Bez wygranej = zero kosztów.',
          '3-5% deal value — invoice only when bidder wins with your product. No win = no cost.'),
        benefit: t(lang, 'Pay-on-success — zero ryzyka', 'Pay-on-success — zero risk'),
      },
      {
        icon: TrendingUp,
        color: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300',
        title: '4. ' + t(lang, 'Market intelligence', 'Market intelligence'),
        description: t(lang,
          'Trendy popytu, średnie ceny w Twojej branży, top buyers (Lasy Państwowe, MEiN, etc.). Co kwartał raport.',
          'Demand trends, average prices in your industry, top buyers. Quarterly report.'),
        benefit: t(lang, 'Wiedza branżowa = przewaga negocjacyjna', 'Industry knowledge = negotiation edge'),
      },
      {
        icon: Send,
        color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900 dark:text-cyan-300',
        title: '5. ' + t(lang, 'Direct RFQ inquiries', 'Direct RFQ inquiries'),
        description: t(lang,
          'Gdy bidder potrzebuje produktu z Twojej kategorii, MASKIL automatycznie wysyła RFQ. Odpowiadasz, rozmawiamy.',
          'When bidder needs product in your category, MASKIL auto-sends RFQ. You reply, we talk.'),
        benefit: t(lang, 'Pasywny lead gen — bez cold callingu', 'Passive lead gen — no cold calling'),
      },
      {
        icon: Check,
        color: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300',
        title: t(lang, 'Gotowy!', 'Ready!'),
        description: t(lang,
          'Zacznę od pokazania jak działa MASKIL od strony bidder, żebyś rozumiał kontekst. Mission Board pokaże dalej.',
          'I will start by showing bidder side first, for context. Mission Board takes it from there.'),
        benefit: null,
      },
    ];
  }

  // Default: bidder (handlowiec)
  return [
    {
      icon: Lightbulb,
      color: 'bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-300',
      title: greeting + ' ' + t(lang, 'Witaj w MASKIL', 'Welcome to MASKIL'),
      description: t(lang,
        'System, który zamieni 2 dni Twojej pracy nad przetargiem w 2 godziny. Pokażę Ci jak — w 5 krokach.',
        'System that turns 2 days of tender work into 2 hours. I will show you how — in 5 steps.'),
      benefit: null,
    },
    {
      icon: Building2,
      color: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
      title: '1. ' + t(lang, 'Discovery 24/7', 'Discovery 24/7'),
      description: t(lang,
        'BZP + TED + GUNB + KPO skanowane codziennie. AI ocenia match_score (0-100) i typuje top-10 dla Ciebie.',
        'BZP + TED + GUNB + KPO scanned daily. AI scores match (0-100), picks top-10 for you.'),
      benefit: t(lang, '95% coverage zamiast 10% ręcznie', '95% coverage vs 10% manual'),
    },
    {
      icon: FileText,
      color: 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300',
      title: '2. ' + t(lang, 'Analiza SWZ przez AI', 'AI SWZ analysis'),
      description: t(lang,
        'Adam czyta 80-stronicowy SWZ i wyciąga: BoQ, wadium, kryteria, red flags. Wszystko w 90 sekund.',
        'Adam reads 80-page SWZ and extracts: BoQ, deposit, criteria, red flags. All in 90 seconds.'),
      benefit: t(lang, 'Oszczędzisz 2 godziny przy każdym SWZ', 'Save 2 hours on every SWZ'),
    },
    {
      icon: Truck,
      color: 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300',
      title: '3. ' + t(lang, 'RFQ do dostawców', 'RFQ to suppliers'),
      description: t(lang,
        'Wybierasz 5 dostawców z bazy 70 firm. AI generuje email + PDF. Wysłane z ada@maskil.pl. Quote auto-parsuje.',
        'Pick 5 suppliers from 70-firm KB. AI generates email + PDF. Sent from ada@maskil.pl. Quotes auto-parsed.'),
      benefit: t(lang, 'Lepsze ceny — 5 ofert zamiast 2', 'Better prices — 5 quotes vs 2'),
    },
    {
      icon: Target,
      color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900 dark:text-cyan-300',
      title: '4. ' + t(lang, 'Verdict + sensitivity slider', 'Verdict + sensitivity slider'),
      description: t(lang,
        'Adam mówi: BID lub PASS z reasoning. Suwak pokazuje co stanie się z marżą gdy obniżysz cenę o 5/10/15%.',
        'Adam says: BID or PASS with reasoning. Slider shows margin impact at -5/-10/-15% price.'),
      benefit: t(lang, 'Decyzja oparta na danych, nie czuju', 'Data-driven decision, not gut feeling'),
    },
    {
      icon: Send,
      color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-300',
      title: '5. ' + t(lang, 'Oferta + JEDZ XML', 'Offer + JEDZ XML'),
      description: t(lang,
        'AI generuje pełną ofertę PDF + JEDZ XML zgodny z dyrektywą UE. Wgrywasz do BZP. Mark submitted. Adam śledzi.',
        'AI generates full offer PDF + EU-compliant JEDZ XML. Upload to BZP. Mark submitted. Adam tracks.'),
      benefit: t(lang, 'JEDZ generator oszczędza 30 min per oferta', 'JEDZ generator saves 30 min per offer'),
    },
    {
      icon: Check,
      color: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300',
      title: t(lang, 'Gotowy!', 'Ready!'),
      description: t(lang,
        'Mam już dla Ciebie gotowy przykład — Pyrzyce 850k PLN. Mission Board pokaże jak przejść pełen flow.',
        'Pre-built example for you — Pyrzyce 850k PLN. Mission Board guides full flow.'),
      benefit: null,
    },
  ];
}


export default function WelcomeModal({ visible, persona = 'bidder', userName, onComplete, onSkip, lang = 'pl' }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const steps = getStepsForPersona(persona, userName, lang);

  if (!visible) return null;

  const s = steps[step];
  const Icon = s.icon;
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const progress = ((step + 1) / steps.length) * 100;

  async function handleComplete() {
    setSubmitting(true);
    try {
      // Mark welcome as seen on backend
      await api.post('/tutorial/welcome-seen').catch(() => null);
      // Initialize DEMO clone (idempotent)
      const demoRes = await api.post('/tutorial/init-demo').catch(() => null);
      const demoOppId = demoRes?.data?.demo_opp_id;

      if (onComplete) onComplete();

      // Route based on persona
      if (persona === 'producer') {
        navigate('/suppliers');
      } else if (persona === 'investor') {
        navigate('/tenders?sort=match&min_score=40');
      } else {
        // bidder default — start with DEMO tender
        if (demoOppId) {
          navigate(`/tenders/${demoOppId}`);
        } else {
          navigate('/tenders/TEST-LED-001');
        }
      }
    } catch (err) {
      console.error('Welcome complete error:', err);
    }
    setSubmitting(false);
  }

  function handleSkip() {
    if (onSkip) onSkip();
  }

  return (
    <>
      {/* Dark overlay */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] transition-opacity duration-300"
        onClick={handleSkip}
      />

      {/* Modal card */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101]
                      bg-card border border-border rounded-2xl shadow-2xl
                      w-[520px] max-w-[92vw] overflow-hidden">

        {/* Progress bar */}
        <div className="h-1 bg-muted">
          <div
            className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted transition-colors text-muted-foreground"
          title={t(lang, 'Pokaż później', 'Show later')}
          data-testid="welcome-modal-skip"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Content */}
        <div className="px-8 pt-10 pb-4 text-center">
          {/* Icon circle */}
          <div className={`w-16 h-16 rounded-2xl ${s.color} flex items-center justify-center mx-auto mb-5`}>
            <Icon className="h-8 w-8" />
          </div>

          {/* 2026-05-27 (T7/C): step indicator usunięty — z 3 kroków nie wnosi
             wartości, a dodaje szum. Progress bar na górze wystarcza. */}

          {/* Title */}
          <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk' }}>
            {s.title}
          </h2>

          {/* Description */}
          <p className="text-sm text-muted-foreground leading-relaxed mb-4 max-w-md mx-auto">
            {s.description}
          </p>

          {/* Benefit badge */}
          {s.benefit && (
            <div className="inline-flex items-center gap-1.5 bg-primary/5 border border-primary/20 rounded-full px-4 py-1.5 mb-2">
              <Clock className="h-3 w-3 text-primary" />
              <span className="text-xs font-medium text-primary">{s.benefit}</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="px-6 pb-5 flex items-center justify-between">
          <Button
            variant="ghost" size="sm"
            onClick={() => setStep(step - 1)}
            disabled={isFirst}
            className={isFirst ? 'invisible' : ''}
            data-testid="welcome-modal-back"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> {t(lang, 'Wstecz', 'Back')}
          </Button>

          {/* Progress dots */}
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === step ? 'bg-primary w-6'
                    : i < step ? 'bg-primary/30 w-2'
                    : 'bg-muted w-2'
                }`}
              />
            ))}
          </div>

          <Button
            size="sm"
            onClick={isLast ? handleComplete : () => setStep(step + 1)}
            disabled={submitting}
            data-testid={isLast ? 'welcome-modal-complete' : 'welcome-modal-next'}
          >
            {isLast ? (
              <><Check className="h-4 w-4 mr-1" /> {t(lang, 'Zaczynamy!', 'Let\'s go!')}</>
            ) : (
              <>{t(lang, 'Dalej', 'Next')} <ChevronRight className="h-4 w-4 ml-1" /></>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
